import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { notesApi } from '@/lib/notesApi';

// Shared 100% gold rate choice for Excel exports: typed rate > portal rate for the chosen date.
const today = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
let state = { date: today(), info: null, loading: false, pick: '', typed: '' };
const subs = new Set();
const set = (p) => { state = { ...state, ...p }; subs.forEach((f) => f()); };
const subscribe = (f) => { subs.add(f); return () => subs.delete(f); };
export const useGoldRate = () => useSyncExternalStore(subscribe, () => state);
export const setGoldTyped = (typed) => set({ typed });

export async function loadGoldRate(date = state.date) {
  set({ date, loading: true, pick: '' });
  try { set({ info: (await notesApi.get('/notes/tag-workspace/gold-rate', { params: { date } })).data }); } catch { set({ info: null }); }
  set({ loading: false });
}

const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '');
const inr = (n) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

// → { rate, source } for the 100% gold rate in use, or null.
export function currentGold(s = state) {
  const t = Number(s.typed);
  if (s.typed !== '' && t > 0) return { rate: t, source: 'typed by you' };
  const i = s.info;
  const r = i?.exact || (s.pick === 'before' ? i?.before : s.pick === 'after' ? i?.after : null);
  return r ? { rate: r.rate, source: `portal rate of ${fmtDate(r.date)}` } : null;
}

// Purity name → % of pure gold ("18Kt" → 75), using the portal's purity master first.
export function purityPercent(name, s = state) {
  const n = String(name || '').trim();
  if (!n) return null;
  const m = s.info?.purities || {};
  const hit = Object.keys(m).find((k) => k.toLowerCase() === n.toLowerCase() || k.replace(/\s+/g, '').toLowerCase() === n.replace(/\s+/g, '').toLowerCase());
  if (hit) return m[hit];
  if (s.info?.purity_console) return null; // the portal's purity console governs: unknown purity has no %
  const kt = n.match(/^(\d+(?:\.\d+)?)\s*k/i);
  if (kt) return Math.round((Number(kt[1]) / 24) * 10000) / 100;
  const num = n.match(/^(\d+(?:\.\d+)?)/);
  if (!num) return null;
  const v = Number(num[1]);
  return v <= 100 ? v : v > 100 && v <= 1000 ? v / 10 : null;
}

function Choice({ label, r, k, s, testid }) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 px-2 py-1.5 text-xs" data-testid={testid}>
      <input type="radio" name="gold-pick" checked={s.pick === k} onChange={() => set({ pick: k })} />
      <span>{label} · <b>{inr(r.rate)}</b> ({fmtDate(r.date)})</span>
    </label>
  );
}

function Panel({ s }) {
  const i = s.info;
  return (
    <div className="space-y-2 text-xs text-slate-700">
      <label className="block">Gold rate date
        <input type="date" value={s.date} max={today()} onChange={(e) => e.target.value && loadGoldRate(e.target.value)} className="mt-1 h-8 w-full rounded-md border border-slate-200 px-2" data-testid="gold-rate-date" />
      </label>
      {s.loading ? <div className="text-slate-500">Loading…</div> : i?.exact ? (
        <div className="rounded-md bg-emerald-50 px-2 py-1.5" data-testid="gold-rate-exact">Portal rate (100%) on {fmtDate(i.exact.date)}: <b>{inr(i.exact.rate)}</b>/g</div>
      ) : i?.before || i?.after ? (
        <div className="space-y-1.5" data-testid="gold-rate-nearby">
          <div className="text-amber-700">No portal rate on {fmtDate(s.date)} — pick one:</div>
          {i.before ? <Choice label="Latest before" r={i.before} k="before" s={s} testid="gold-rate-pick-before" /> : null}
          {i.after ? <Choice label="First after" r={i.after} k="after" s={s} testid="gold-rate-pick-after" /> : null}
        </div>
      ) : <div className="text-slate-500" data-testid="gold-rate-none">No gold rate in the portal — type one below.</div>}
      <label className="block">Or type 100% gold rate per g (used first)
        <input type="number" min="0" step="0.01" value={s.typed} onChange={(e) => set({ typed: e.target.value })} placeholder="e.g. 7200" className="mt-1 h-8 w-full rounded-md border border-slate-200 px-2" data-testid="gold-rate-typed" />
      </label>
    </div>
  );
}

// Viewport position for the portal panel: below the button, or above when there is no room.
const fixedPos = (r) => {
  const right = Math.max(8, window.innerWidth - r.right);
  return window.innerHeight - r.bottom > 340 ? { top: r.bottom + 4, right } : { bottom: window.innerHeight - r.top + 4, right };
};

// Compact button + dropdown for choosing the 100% gold rate before an Excel download.
export function GoldRatePicker({ className = '', testid = 'gold-rate-picker', up = false, portal = false }) {
  const s = useGoldRate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const panelRef = useRef(null);
  const [pos, setPos] = useState(null);
  useEffect(() => { if (!s.info && !s.loading) loadGoldRate(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (!ref.current?.contains(e.target) && !panelRef.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') { e.stopImmediatePropagation(); e.preventDefault(); setOpen(false); } };
    document.addEventListener('mousedown', close);
    window.addEventListener('keydown', esc, true);
    return () => { document.removeEventListener('mousedown', close); window.removeEventListener('keydown', esc, true); };
  }, [open]);
  const g = currentGold(s);
  const panel = (cls, style) => (
    <div style={style} className={`${cls} z-[300] w-72 max-w-[85vw] rounded-lg border border-slate-200 bg-white p-3 shadow-xl`} data-testid={`${testid}-panel`}>
      <Panel s={s} />
      {g ? <div className="mt-2 text-[11px] text-slate-500" data-testid={`${testid}-source`}>Using {inr(g.rate)}/g ({g.source}), converted to each piece's purity.</div> : null}
    </div>
  );
  return (
    <div ref={ref} className={`relative inline-block ${className}`}>
      <button type="button" onClick={() => { if (portal && !open) setPos(fixedPos(ref.current.getBoundingClientRect())); setOpen(!open); }} title="100% gold rate used for net weight amount"
        className={`h-9 w-full rounded-md border px-2 text-left text-xs ${g ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-slate-200 bg-white text-slate-700'}`} data-testid={testid}>
        {g ? `Gold 100%: ${inr(g.rate)}` : 'Gold rate: choose'}
      </button>
      {open ? (portal ? createPortal(<div ref={panelRef} className="tag-scope">{panel('fixed', pos)}</div>, document.body) : panel(`absolute right-0 ${up ? 'bottom-full mb-1' : 'mt-1'}`)) : null}
    </div>
  );
}
